/**
 * Secure Storage Service
 * Provides Keychain-backed storage on iOS, falls back to localStorage on web
 * Used for storing sensitive data like auth tokens securely
 */

import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import logger from '../../utils/logger';

const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * Secure storage wrapper that uses Keychain on iOS, localStorage on web
 */
export const secureStorage = {
  /**
   * Get a value from secure storage
   * @param {string} key - The key to retrieve
   * @returns {Promise<string|null>} The value or null if not found
   */
  async get(key) {
    try {
      if (IS_NATIVE) {
        const result = await SecureStoragePlugin.get({ key });
        return result.value;
      }
      return localStorage.getItem(key);
    } catch (error) {
      // SecureStoragePlugin throws if key doesn't exist
      const err = /** @type {Error & { code?: string }} */ (error);
      if (err.message?.includes('not found') || err.code === 'ERR_NO_VALUE') {
        return null;
      }
      logger.warn('secureStorage.get failed:', error);
      // Fallback to localStorage on native if Keychain fails
      if (IS_NATIVE) {
        try {
          return localStorage.getItem(key);
        } catch (_) {
          return null;
        }
      }
      return null;
    }
  },

  /**
   * Set a value in secure storage
   * @param {string} key - The key to store
   * @param {string} value - The value to store
   * @returns {Promise<boolean>} True if successful
   */
  async set(key, value) {
    try {
      if (IS_NATIVE) {
        await SecureStoragePlugin.set({ key, value });
        return true;
      }
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      logger.warn('secureStorage.set failed:', error);
      // Fallback to localStorage on native if Keychain fails
      if (IS_NATIVE) {
        try {
          localStorage.setItem(key, value);
          return true;
        } catch (_) {
          return false;
        }
      }
      return false;
    }
  },

  /**
   * Remove a value from secure storage
   * @param {string} key - The key to remove
   * @returns {Promise<boolean>} True if successful
   */
  async remove(key) {
    try {
      if (IS_NATIVE) {
        await SecureStoragePlugin.remove({ key });
        return true;
      }
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      logger.warn('secureStorage.remove failed:', error);
      // Fallback to localStorage on native
      if (IS_NATIVE) {
        try {
          localStorage.removeItem(key);
          return true;
        } catch (_) {
          return false;
        }
      }
      return false;
    }
  },

  /**
   * Clear all secure storage
   * @returns {Promise<boolean>} True if successful
   */
  async clear() {
    try {
      if (IS_NATIVE) {
        await SecureStoragePlugin.clear();
        return true;
      }
      // On web, only clear auth-related keys to avoid nuking other data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('auth'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      return true;
    } catch (error) {
      logger.warn('secureStorage.clear failed:', error);
      return false;
    }
  },

  /**
   * Check if running on native platform
   * @returns {boolean}
   */
  isNative() {
    return IS_NATIVE;
  },
};

/**
 * Custom storage adapter for Supabase auth that uses secure storage
 * Implements the Storage interface expected by Supabase
 */
export const secureSupabaseStorage = {
  /** @param {string} key */
  getItem: async (key) => {
    const value = await secureStorage.get(key);
    return value;
  },
  /** @param {string} key @param {string} value */
  setItem: async (key, value) => {
    await secureStorage.set(key, value);
  },
  /** @param {string} key */
  removeItem: async (key) => {
    await secureStorage.remove(key);
  },
};

/**
 * Migrate existing tokens from localStorage to secure storage (iOS only)
 * Call this once on app startup to ensure tokens are in Keychain
 */
export async function migrateToSecureStorage() {
  if (!IS_NATIVE) {
    return; // Only needed on native
  }

  const supabaseKey = 'sb-fkrihbmobplssbwiunif-auth-token';

  try {
    // Check if token exists in localStorage but not in secure storage
    const localToken = localStorage.getItem(supabaseKey);
    if (!localToken) {
      return; // Nothing to migrate
    }

    // Check if already migrated
    const secureToken = await secureStorage.get(supabaseKey);
    if (secureToken) {
      // Already in secure storage, remove from localStorage
      localStorage.removeItem(supabaseKey);
      logger.info('Auth token already in Keychain, removed from localStorage');
      return;
    }

    // Migrate: copy to secure storage, then remove from localStorage
    await secureStorage.set(supabaseKey, localToken);
    localStorage.removeItem(supabaseKey);
    logger.info('Auth token migrated to Keychain');
  } catch (error) {
    logger.warn('Token migration to secure storage failed:', error);
    // Don't throw - auth will still work with localStorage fallback
  }
}

export default secureStorage;
